import React, { useEffect, useState } from "react";
import { Stack, Typography, Button, Box, Menu, OutlinedInput, Modal, IconButton, Chip } from "@mui/material";
import { ReactComponent as ActionIcon } from "../../../../vendor/_new/icon/sites/action.svg";
import { ReactComponent as DomainIcon } from "../../../../vendor/_new/icon/sites/manage_domain.svg";
import { ReactComponent as PlusIcon } from "../../../../vendor/_new/icon/sites/plus.svg";
import { ReactComponent as InfoIcon } from "../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as DeleteIcon } from "../../../../vendor/_new/icon/sites/delete.svg";
import { ReactComponent as EditIcon } from "../../../../vendor/_new/icon/sites/edit.svg";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as CheckIcon } from "../../../../vendor/_new/icon/sites/check.svg";
import { ReactComponent as OriginIcon } from "../../../../vendor/_new/icon/sites/origin.svg";

import colors from "../../../../utils/colors";
import useSite from "../../../../hooks/user/useSite";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";
import { useNotification } from "../../../../contexts/NotificationContext";

function toSubdomainPayload(sub) {
  return {
    name: String(sub?.name || "").trim(),
    addr: String(sub?.addr || "").trim(),
    enabled: sub?.enabled !== undefined ? !!sub.enabled : true,
  };
}

function parseSiteUpdateError(err) {
  if (typeof err === "string") return err;
  if (err?.message) return err.message;
  return "Failed to save subdomains";
}

/** Merge pending inline edit/add, validate, and map to API shape. */
function buildSubdomainsForSave(subdomains, subdomainToEdit, subdomainToAdd) {
  let list = [...subdomains];

  if (subdomainToEdit) {
    const idx = list.findIndex((s) => s.id === subdomainToEdit.id);
    if (idx >= 0) list[idx] = subdomainToEdit;
  }

  if (subdomainToAdd) {
    const draft = toSubdomainPayload(subdomainToAdd);
    if (!draft.name || !draft.addr) {
      throw new Error("Each subdomain needs a name and origin address before saving.");
    }
    if (!list.some((s) => s.id === subdomainToAdd.id)) {
      list = [...list, subdomainToAdd];
    }
  }

  const payload = list.map(toSubdomainPayload).filter((s) => s.name);
  const incomplete = payload.find((s) => !s.addr);
  if (incomplete) {
    throw new Error("Each subdomain needs a name and origin address before saving.");
  }
  return payload;
}

const SectionCard = ({ title, action, children }) => (
  <Box
    sx={{
      backgroundColor: colors.neutral[50],
      borderRadius: "12px",
      border: `1px solid ${colors.neutral[200]}`,
      overflow: "hidden",
    }}
  >
    <Stack 
      direction="row" 
      alignItems="center" 
      justifyContent="space-between"
      sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.neutral[200]}` }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <DomainIcon style={{ width: 20, height: 20, color: "#6371F7" }} />
        <Typography sx={ { fontSize: 15, fontWeight: "medium", color: colors.neutral[1000] }}>
          {title}
        </Typography>
      </Stack>
      {action}
    </Stack>
    <Box sx={{ p: 3 }}>
      {children}
    </Box>
  </Box>
);

const SubdomainCard = ({ subdomain, siteName, onEdit, onAction, isEditing, editData, setEditData, onSaveEdit, onCancelEdit, canEdit }) => {
  if (isEditing) {
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: "8px",
          backgroundColor: "#fff",
          border: `1px solid #6371F7`,
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <OutlinedInput
              size="small"
              placeholder="subdomain"
              value={editData?.name || ""}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              sx={{
                flex: 1,
                height: 36,
                fontFamily: "monospace",
                fontSize: 13,
                "& .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: "8px",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: `1px solid #6371F7`,
                },
              }}
            />
            <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
              .{siteName}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
              <OriginIcon style={{ width: 16, height: 16, color: colors.neutral[500] }} />
              <OutlinedInput
                size="small"
                placeholder="origin.example.com"
                value={editData?.addr || ""}
                onChange={(e) => setEditData({ ...editData, addr: e.target.value })}
                sx={{
                  flex: 1,
                  height: 36,
                  fontFamily: "monospace",
                  fontSize: 13,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: `1px solid ${colors.neutral[300]}`,
                    borderRadius: "8px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: `1px solid #6371F7`,
                  },
                }}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={ { fontSize: 13, color: colors.neutral[600] }}>WAF</Typography>
              <Box
                onClick={() => setEditData({ ...editData, enabled: !editData?.enabled })}
                sx={{
                  width: 40,
                  height: 22,
                  borderRadius: "11px",
                  backgroundColor: editData?.enabled ? "#6371F7" : colors.neutral[300],
                  position: "relative",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    position: "absolute",
                    top: 3,
                    left: editData?.enabled ? 21 : 3,
                    transition: "left 0.2s",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                  }}
                />
              </Box>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <IconButton size="small" onClick={onCancelEdit} sx={{ color: colors.neutral[600] }}>
              <CloseIcon style={{ width: 18, height: 18 }} />
            </IconButton>
            <IconButton size="small" onClick={onSaveEdit} sx={{ color: "#10B981" }}>
              <CheckIcon style={{ width: 18, height: 18 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: "8px",
        backgroundColor: "#fff",
        border: `1px solid ${colors.neutral[200]}`,
        transition: "border-color 0.2s",
        "&:hover": {
          borderColor: colors.neutral[400],
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack spacing={1}>
          <Typography sx={{ fontFamily: "monospace", fontSize: 14, fontWeight: "medium", color: "#6371F7" }}>
            {subdomain?.name}.{siteName}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <OriginIcon style={{ width: 14, height: 14, color: colors.neutral[500] }} />
            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: colors.neutral[600] }}>
              {subdomain?.addr}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Chip
            label={subdomain?.enabled ? "WAF Active" : "WAF Disabled"}
            size="small"
            sx={{
              backgroundColor: subdomain?.enabled ? "#D1FAE5" : "#FEE2E2",
              color: subdomain?.enabled ? "#065F46" : "#B91C1C",
              fontSize: 11,
              fontWeight: "medium",
              height: 24,
            }}
          />
          {canEdit && (
            <IconButton size="small" onClick={onAction}>
              <ActionIcon style={{ width: 18, height: 18, color: colors.neutral[600] }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

const ManageDomainSubdomain = ({ site, handleManageDomainClose, canEdit: canEditProp }) => {
  const { setWebsiteController } = useAuth();
  const { getSites, updateSite, getSitesForList } = useSite();
  const { hasPermission, PERMISSIONS } = usePermissions();
  const { showSuccess, showError } = useNotification();
  const [subdomains, setSubdomains] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Use passed permission or fall back to global check
  const canEdit = canEditProp !== undefined ? canEditProp : hasPermission(PERMISSIONS.SITES_WRITE);

  const [actionMenu, setActionMenu] = useState({ anchorEl: null, subdomainId: null });
  const handleActionOpen = (event, subdomainId = null) => {
    event.stopPropagation();
    setActionMenu({ anchorEl: event.currentTarget, subdomainId });
  };
  const handleActionClose = () => setActionMenu({ anchorEl: null, subdomainId: null });
  const isActionMenuOpen = Boolean(actionMenu.anchorEl);

  const [subdomainToEdit, setSubdomainToEdit] = useState(null);
  const [subdomainToAdd, setSubdomainToAdd] = useState(null);
  const [subdomainToDelete, setSubdomainToDelete] = useState(null);
  const [openSubdomainToDelete, setOpenSubdomainToDelete] = useState(false);

  useEffect(() => {
    if (!site) {
      setSubdomains([]);
    } else {
      setSubdomains(
        site?.subdomains?.map((subdomain, index) => ({
          id: index + 1,
          name: subdomain.name,
          addr: subdomain.addr,
          enabled: subdomain?.enabled,
        })) || []
      );
    }
  }, [site]);

  const handleDelete = () => {
    setSubdomains((prev) => prev?.filter((item) => item?.id !== subdomainToDelete?.id));
    setSubdomainToDelete(null);
    setOpenSubdomainToDelete(false);
  };

  const handleClose = () => {
    setSubdomainToEdit(null);
    setSubdomainToAdd(null);
    setSubdomainToDelete(null);
    handleManageDomainClose();
  };

  const saveDomains = async () => {
    setSaving(true);
    try {
      const payload = buildSubdomainsForSave(subdomains, subdomainToEdit, subdomainToAdd);

      if (subdomainToEdit) {
        setSubdomains((prev) => prev.map((s) => (s.id === subdomainToEdit.id ? subdomainToEdit : s)));
        setSubdomainToEdit(null);
      }
      if (subdomainToAdd?.name && subdomainToAdd?.addr) {
        setSubdomains((prev) => (prev.some((s) => s.id === subdomainToAdd.id) ? prev : [...prev, subdomainToAdd]));
        setSubdomainToAdd(null);
      }

      await updateSite(site.id, {}, payload);
      await Promise.all([
        getSitesForList(),
        getSites(setWebsiteController),
      ]);
      showSuccess("Subdomains saved successfully");
      handleClose();
    } catch (err) {
      showError(parseSiteUpdateError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleAddSubdomain = () => {
    const newId = subdomains.length > 0 ? Math.max(...subdomains.map((s) => s.id)) + 1 : 1;
    setSubdomainToAdd({ name: "", addr: "", enabled: true, id: newId });
  };

  return (
    <>
      <Stack spacing={3} justifyContent="space-between" sx={{ height: "100%" }}>
        <Stack spacing={3}>
          <SectionCard
            title="Subdomains"
            action={
              canEdit && (
                <Button
                  onClick={handleAddSubdomain}
                  startIcon={<PlusIcon style={{ width: 16, height: 16 }} />}
                  sx={{
                  height: 32,
                  px: 2,
                  backgroundColor: "rgba(99, 113, 247, 0.1)",
                  borderRadius: "6px",
                  fontSize: 13,
                  color: "#6371F7",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(99, 113, 247, 0.2)",
                  },
                }}
              >
                  Add Subdomain
                </Button>
              )
            }
          >
            <Stack spacing={2}>
              {subdomainToAdd && (
                <SubdomainCard
                  siteName={site?.name || site?.site_id}
                  isEditing={true}
                  editData={subdomainToAdd}
                  setEditData={setSubdomainToAdd}
                  onSaveEdit={() => {
                    if (subdomainToAdd.name && subdomainToAdd.addr) {
                      setSubdomains((prev) => [...prev, subdomainToAdd]);
                      setSubdomainToAdd(null);
                    }
                  }}
                  onCancelEdit={() => setSubdomainToAdd(null)}
                />
              )}
              {subdomains.length === 0 && !subdomainToAdd ? (
                <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                  <DomainIcon style={{ width: 40, height: 40, color: colors.neutral[400] }} />
                  <Typography sx={ { fontSize: 14, color: colors.neutral[600] }}>
                    No subdomains configured
                  </Typography>
                </Stack>
              ) : (
                subdomains.map((subdomain) => (
                  <SubdomainCard
                    key={subdomain.id}
                    subdomain={subdomain}
                    siteName={site?.name || site?.site_id}
                    isEditing={subdomainToEdit?.id === subdomain.id}
                    editData={subdomainToEdit?.id === subdomain.id ? subdomainToEdit : null}
                    setEditData={setSubdomainToEdit}
                    onSaveEdit={() => {
                      setSubdomains((prev) => prev.map((s) => (s.id === subdomainToEdit.id ? subdomainToEdit : s)));
                      setSubdomainToEdit(null);
                    }}
                    onCancelEdit={() => setSubdomainToEdit(null)}
                    onAction={(e) => handleActionOpen(e, subdomain.id)}
                    canEdit={canEdit}
                  />
                ))
              )}
            </Stack>
          </SectionCard>

          <Box
            sx={{
              p: 2.5,
              borderRadius: "8px",
              backgroundColor: "rgba(99, 113, 247, 0.08)",
              border: "1px solid rgba(99, 113, 247, 0.2)",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <InfoIcon style={{ width: 20, height: 20, color: "#6371F7", flexShrink: 0, marginTop: 2 }} />
              <Typography sx={ { fontSize: 13, lineHeight: 1.6, color: "#6371F7" }}>
                {canEdit ? (
                  <>
                    Subdomains inherit the main domain's security settings by default. 
                    You can customize WAF protection individually for each subdomain.
                  </>
                ) : (
                  "You are viewing subdomains in read-only mode. Contact an administrator to make changes."
                )}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2}>
          {canEdit && (
            <Button
              onClick={saveDomains}
              disabled={saving}
              sx={{
              height: 44,
              flex: 1,
              backgroundColor: "#6371F7",
              borderRadius: "8px",
              fontSize: 14,
              color: "#fff",
              fontWeight: "medium",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#4F5BD5",
              },
            }}
          >
              Save Changes
            </Button>
          )}
          <Button
            onClick={handleClose}
            disabled={saving}
            sx={{
              height: 44,
              flex: 1,
              borderRadius: "8px",
              fontSize: 14,
              color: colors.neutral[700],
              fontWeight: "medium",
              textTransform: "none",
              border: `1px solid ${colors.neutral[300]}`,
              "&:hover": {
                backgroundColor: colors.neutral[100],
                border: `1px solid ${colors.neutral[400]}`,
              },
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>

      <Menu
        anchorEl={actionMenu.anchorEl}
        open={isActionMenuOpen}
        onClose={handleActionClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "8px",
            border: `1px solid ${colors.neutral[200]}`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            minWidth: 140,
          },
          "& .MuiList-root": {
            py: 1,
          },
        }}
      >
        <Stack sx={{ px: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            onClick={() => {
              setSubdomainToEdit(subdomains.find((s) => s.id === actionMenu.subdomainId));
              handleActionClose();
            }}
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: "6px",
              cursor: "pointer",
              "&:hover": { backgroundColor: colors.neutral[100] },
            }}
          >
            <EditIcon style={{ width: 16, height: 16, color: colors.neutral[600] }} />
            <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>Edit</Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            onClick={() => {
              setSubdomainToDelete(subdomains.find((s) => s.id === actionMenu.subdomainId));
              setOpenSubdomainToDelete(true);
              handleActionClose();
            }}
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: "6px",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#FEE2E2" },
            }}
          >
            <DeleteIcon style={{ width: 16, height: 16, color: "#B91C1C" }} />
            <Typography sx={ { fontSize: 14, color: "#B91C1C" }}>Delete</Typography>
          </Stack>
        </Stack>
      </Menu>

      <Modal open={openSubdomainToDelete} onClose={() => setOpenSubdomainToDelete(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 420,
            backgroundColor: "#fff",
            borderRadius: "12px",
            p: 4,
            outline: "none",
          }}
        >
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography sx={ { fontSize: 18, fontWeight: "medium", color: colors.neutral[1000] }}>
                Delete Subdomain
              </Typography>
              <IconButton size="small" onClick={() => setOpenSubdomainToDelete(false)}>
                <CloseIcon style={{ width: 20, height: 20, color: colors.neutral[600] }} />
              </IconButton>
            </Stack>
            <Typography sx={ { fontSize: 14, lineHeight: 1.6, color: colors.neutral[700] }}>
              You're about to delete <strong style={{ color: colors.neutral[1000] }}>{subdomainToDelete?.name}</strong>. 
              This will remove all configurations and WAF rules for this subdomain.
            </Typography>
            <Box sx={{ p: 2, borderRadius: "8px", backgroundColor: "#FEF3C7", border: "1px solid #FCD34D" }}>
              <Typography sx={ { fontSize: 13, color: "#92400E" }}>
                This action cannot be undone.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                onClick={() => setOpenSubdomainToDelete(false)}
                sx={{
                  height: 40,
                  flex: 1,
                  borderRadius: "8px",
                  fontSize: 14,
                  color: colors.neutral[700],
                  textTransform: "none",
                  border: `1px solid ${colors.neutral[300]}`,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                sx={{
                  height: 40,
                  flex: 1,
                  backgroundColor: "#B91C1C",
                  borderRadius: "8px",
                  fontSize: 14,
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#991B1B",
                  },
                }}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};
export default ManageDomainSubdomain;
